import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileUp, Table2, CheckCircle2, X, Edit2, Trash2, AlertTriangle, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const IMPORT_KATEGORIEN = [
  { value: "mitarbeiter", label: "Mitarbeiter / Personal", icon: "👤", felder: ["name", "position", "stundenlohn", "wochenstunden", "anstellungsart"] },
  { value: "fixkosten", label: "Fixkosten", icon: "🏢", felder: ["bezeichnung", "betrag", "kategorie", "intervall"] },
  { value: "zutaten", label: "Zutaten / Artikel", icon: "🧂", felder: ["name", "einheit", "preis_pro_einheit", "lieferant"] },
  { value: "tagesumsatz", label: "Tagesumsatz / Einnahmen", icon: "💰", felder: ["datum", "einnahmen_speisen", "einnahmen_getraenke", "einnahmen_sonstiges"] },
  { value: "einkauf", label: "Einkauf / Rechnungen", icon: "🧾", felder: ["zutat_name", "menge", "einheit", "einkaufspreis", "lieferant", "lieferdatum"] },
];

const FELD_LABELS = {
  name: "Name", position: "Position", stundenlohn: "Stundenlohn (€)", wochenstunden: "Wochenstunden",
  anstellungsart: "Anstellungsart", bezeichnung: "Bezeichnung", betrag: "Betrag (€)", kategorie: "Kategorie",
  intervall: "Intervall", einheit: "Einheit", preis_pro_einheit: "Preis/Einheit (€)", lieferant: "Lieferant",
  datum: "Datum", einnahmen_speisen: "Speisen (€)", einnahmen_getraenke: "Getränke (€)",
  einnahmen_sonstiges: "Sonstiges (€)", gesamt: "Gesamt (€)", gaeste_anzahl: "Gäste", wetter: "Wetter",
  zutat_name: "Artikelname", menge: "Menge", einkaufspreis: "Einkaufspreis (€)", lieferdatum: "Lieferdatum"
};

const DatenImport = () => {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [uploadResult, setUploadResult] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editedData, setEditedData] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.toLowerCase();
    if (!ext.endsWith('.csv') && !ext.endsWith('.pdf')) {
      toast.error("Nur CSV und PDF Dateien werden unterstützt");
      return;
    }

    setUploading(true);
    setUploadResult(null);
    setEditedData([]);

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (selectedCategory) formData.append("target_category", selectedCategory);

      const res = await axios.post(`${API}/import/analyze`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });

      setUploadResult(res.data);
      setEditedData(res.data.vorschau || []);
      if (res.data.erkannte_kategorie && !selectedCategory) {
        setSelectedCategory(res.data.erkannte_kategorie);
      }
      toast.success(`${res.data.erkannte_eintraege} Einträge erkannt`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Analysieren der Datei");
    } finally {
      setUploading(false);
    }
  };

  const handleEditRow = (index) => {
    setEditingRow(index);
    setEditValues({ ...editedData[index] });
  };

  const handleSaveRow = (index) => {
    const updated = [...editedData];
    updated[index] = { ...editValues };
    setEditedData(updated);
    setEditingRow(null);
    setEditValues({});
  };

  const handleDeleteRow = (index) => {
    setEditedData(editedData.filter((_, i) => i !== index));
  };

  const handleConfirmImport = async () => {
    if (editedData.length === 0) {
      toast.error("Keine Daten zum Importieren");
      return;
    }

    setImporting(true);
    try {
      const res = await axios.post(`${API}/import/confirm`, {
        kategorie: selectedCategory || uploadResult?.erkannte_kategorie,
        eintraege: editedData
      }, { withCredentials: true });

      toast.success(res.data.nachricht);
      setUploadResult(null);
      setEditedData([]);
      setSelectedCategory("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Importieren");
    } finally {
      setImporting(false);
    }
  };

  const currentKat = IMPORT_KATEGORIEN.find(k => k.value === (selectedCategory || uploadResult?.erkannte_kategorie));
  const felder = currentKat?.felder || [];

  return (
    <div className="space-y-5" data-testid="datenimport-tab">
      <div className="bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 p-5">
        <h3 className="font-display text-base font-semibold mb-1 dark:text-slate-100">Daten importieren</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Laden Sie CSV- oder PDF-Dateien hoch. Die Daten werden analysiert und zur Überprüfung angezeigt, bevor sie importiert werden.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-[#5C5C5C] dark:text-slate-400">Ziel-Kategorie (optional)</Label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="border-stone-300 dark:border-slate-600 rounded-sm" data-testid="import-category-select">
                <SelectValue placeholder="Automatisch erkennen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Automatisch erkennen</SelectItem>
                {IMPORT_KATEGORIEN.map(k => (
                  <SelectItem key={k.value} value={k.value}>{k.icon} {k.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-[#5C5C5C] dark:text-slate-400">Datei auswählen</Label>
            <div className="flex gap-2">
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv,.pdf"
                onChange={handleFileUpload}
                className="border-stone-300 dark:border-slate-600 rounded-sm"
                data-testid="import-file-input"
              />
            </div>
          </div>
        </div>

        {uploading && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/30 rounded text-sm">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
            <span className="dark:text-blue-300">Datei wird analysiert...</span>
          </div>
        )}
      </div>

      {/* Quick Upload in Category Buttons */}
      {!uploadResult && (
        <div className="bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 p-5">
          <h3 className="font-display text-sm font-semibold mb-3 dark:text-slate-100">Schnellimport nach Kategorie</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {IMPORT_KATEGORIEN.map(k => (
              <button
                key={k.value}
                onClick={() => {
                  setSelectedCategory(k.value);
                  fileInputRef.current?.click();
                }}
                className="flex flex-col items-center gap-2 p-4 border border-stone-200 dark:border-slate-600 rounded hover:bg-stone-50 dark:hover:bg-slate-700 transition-colors text-center"
                data-testid={`quick-import-${k.value}`}
              >
                <FileUp size={20} className="text-slate-500 dark:text-slate-400" />
                <span className="text-xs font-medium dark:text-slate-300">{k.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Preview Results */}
      {uploadResult && editedData.length > 0 && (
        <div className="bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-base font-semibold dark:text-slate-100">
                <Table2 size={16} className="inline mr-2" />
                Vorschau: {editedData.length} Einträge
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Datei: {uploadResult.dateiname} | Kategorie: {currentKat?.label || uploadResult.erkannte_kategorie}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => { setUploadResult(null); setEditedData([]); if (fileInputRef.current) fileInputRef.current.value = ""; }} className="rounded-sm text-xs">
                <X size={14} className="mr-1" />Verwerfen
              </Button>
              <Button
                size="sm"
                onClick={handleConfirmImport}
                disabled={importing}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs"
                data-testid="confirm-import-btn"
              >
                {importing ? (
                  <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full mr-1" />
                ) : (
                  <CheckCircle2 size={14} className="mr-1" />
                )}
                {importing ? "Importiere..." : `${editedData.length} Einträge importieren`}
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[400px] overflow-y-auto border border-stone-100 dark:border-slate-700 rounded">
            <table className="w-full text-xs">
              <thead className="bg-stone-50 dark:bg-slate-700 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300">#</th>
                  {felder.map(f => (
                    <th key={f} className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">
                      {FELD_LABELS[f] || f}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right font-medium text-slate-600 dark:text-slate-300">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 dark:divide-slate-700">
                {editedData.map((row, index) => (
                  <tr key={index} className="hover:bg-stone-50 dark:hover:bg-slate-700/50">
                    <td className="px-3 py-2 text-slate-400">{index + 1}</td>
                    {felder.map(f => (
                      <td key={f} className="px-3 py-2 dark:text-slate-300">
                        {editingRow === index ? (
                          <Input
                            value={editValues[f] ?? ""}
                            onChange={(e) => setEditValues({ ...editValues, [f]: e.target.value })}
                            className="h-7 text-xs border-stone-300 dark:border-slate-600 rounded-sm"
                          />
                        ) : (
                          <span className="font-mono">{row[f] !== undefined && row[f] !== null ? String(row[f]) : "-"}</span>
                        )}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {editingRow === index ? (
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => handleSaveRow(index)} className="h-6 w-6 p-0 text-emerald-600">
                            <Check size={12} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setEditingRow(null)} className="h-6 w-6 p-0 text-slate-400">
                            <X size={12} />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => handleEditRow(index)} className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700">
                            <Edit2 size={12} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteRow(index)} className="h-6 w-6 p-0 text-red-400 hover:text-red-600">
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Bitte überprüfen Sie alle Einträge auf Richtigkeit, bevor Sie den Import bestätigen. Sie können einzelne Zeilen bearbeiten oder löschen.
            </p>
          </div>
        </div>
      )}

      {/* Empty result message */}
      {uploadResult && editedData.length === 0 && (
        <div className="bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 p-8 text-center">
          <AlertTriangle size={32} className="mx-auto text-amber-500 mb-3" />
          <p className="font-medium dark:text-slate-200">Keine Daten erkannt</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Die Datei konnte nicht analysiert werden. Bitte prüfen Sie das Format.
          </p>
          {uploadResult.rohtext_vorschau && (
            <div className="mt-3 p-3 bg-stone-50 dark:bg-slate-700 rounded text-left text-xs font-mono overflow-auto max-h-32 dark:text-slate-300">
              {uploadResult.rohtext_vorschau}
            </div>
          )}
        </div>
      )}

      {/* CSV format hints */}
      <div className="bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 p-5">
        <h3 className="font-display text-sm font-semibold mb-3 dark:text-slate-100">CSV-Format Hinweise</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-slate-600 dark:text-slate-400">
          <div>
            <p className="font-medium mb-1 dark:text-slate-300">Mitarbeiter:</p>
            <code className="block bg-stone-50 dark:bg-slate-700 p-2 rounded font-mono dark:text-slate-300">Name;Position;Stundenlohn;Wochenstunden;Anstellungsart</code>
          </div>
          <div>
            <p className="font-medium mb-1 dark:text-slate-300">Fixkosten:</p>
            <code className="block bg-stone-50 dark:bg-slate-700 p-2 rounded font-mono dark:text-slate-300">Bezeichnung;Betrag;Kategorie;Intervall</code>
          </div>
          <div>
            <p className="font-medium mb-1 dark:text-slate-300">Zutaten:</p>
            <code className="block bg-stone-50 dark:bg-slate-700 p-2 rounded font-mono dark:text-slate-300">Name;Einheit;Preis_pro_Einheit;Lieferant</code>
          </div>
          <div>
            <p className="font-medium mb-1 dark:text-slate-300">Tagesumsatz:</p>
            <code className="block bg-stone-50 dark:bg-slate-700 p-2 rounded font-mono dark:text-slate-300">Datum;Einnahmen_Speisen;Einnahmen_Getraenke;Einnahmen_Sonstiges</code>
          </div>
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-3">Trennzeichen: Semikolon (;) oder Komma (,) | Zahlenformat: 1234,56 (deutsch) oder 1234.56</p>
      </div>
    </div>
  );
};

export default DatenImport;
